import { WebDashboardSummary } from "@coeff/api";
import moment from "moment";
import React, { useEffect, useState } from "react";

import { NewWindow } from "../../../components";
import {
  DARK_FADE_TOP_PERCENT,
  FADE_HEIGHT_FRACTION,
  getLegacyFadeStopPercent,
} from "../../../utils";
import { MobileBottomSheet } from "../MobileBottomSheet";

/**
 * Row thumbnail, sharing the list page's treatment at popover scale: the box is 16:9 (matching the
 * generated 1440x810 captures, which fill it exactly), while a legacy capture is fit to the box's
 * WIDTH and top-aligned rather than cover-cropped — cover would slice both outer KPI columns off —
 * with its bottom dissolved into the surface. The popover lives in the THEMED viewer, so the fade
 * dissolves to the theme's background variable, not a hard-coded white.
 *
 * The fallback placeholder art is already 16:9, so it never fades.
 */
const DashboardRowThumbnail: React.FC<{
  screenshotUrl?: string | null;
  title: string;
}> = ({ screenshotUrl, title }) => {
  const [fadeStopPercent, setFadeStopPercent] = useState<number | undefined>(undefined);

  useEffect(() => {
    setFadeStopPercent(undefined);
  }, [screenshotUrl]);

  return (
    <div className="relative flex-shrink-0 w-20 aspect-video rounded overflow-hidden bg-main-background-secondary border border-main-border">
      {screenshotUrl ? (
        <img
          src={screenshotUrl}
          alt={title}
          className={
            fadeStopPercent === undefined
              ? "w-full h-full object-cover"
              : "w-full h-auto object-top"
          }
          onLoad={e => {
            const img = e.currentTarget;
            setFadeStopPercent(getLegacyFadeStopPercent(img.naturalWidth, img.naturalHeight));
          }}
        />
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            backgroundImage: "url(../../../static/dashboard-thumbnail.png)",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: "cover",
          }}
        />
      )}
      {fadeStopPercent !== undefined && (
        <>
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `linear-gradient(to bottom, transparent ${
                fadeStopPercent - FADE_HEIGHT_FRACTION * 100
              }%, var(--color-main-background, #ffffff) ${fadeStopPercent}%)`,
            }}
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `linear-gradient(to bottom, transparent ${DARK_FADE_TOP_PERCENT}%, rgba(0, 0, 0, 0.2))`,
            }}
          />
        </>
      )}
    </div>
  );
};

interface MyDashboardsPopoverProps {
  visible: boolean;
  dashboards: WebDashboardSummary[] | undefined;
  isLoading: boolean;
  dropdownRef: React.RefObject<HTMLDivElement>;
  onClose: () => void;
  /** Iframe document — enables the mobile bottom-sheet presentation and correct portal target. */
  targetDocument?: Document;
}

export const MyDashboardsPopover: React.FC<MyDashboardsPopoverProps> = ({
  visible,
  dashboards,
  isLoading,
  dropdownRef,
  onClose,
  targetDocument,
}) => {
  if (!visible) {
    return null;
  }

  return (
    <MobileBottomSheet
      onClose={onClose}
      targetDocument={targetDocument}
      containerRef={dropdownRef}
      ariaLabel="My Dashboards"
      className="bg-main-background overflow-hidden"
      desktopClassName="absolute z-[210] shadow-2xl border border-main-border rounded-lg w-[420px] top-[calc(100%+8px)] right-0 max-h-[calc(100vh-72px)]"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-main-border flex-shrink-0 sticky top-0 bg-main-background z-10">
        <div className="text-base font-semibold text-main-foreground">My Dashboards</div>
        <button
          type="button"
          onClick={onClose}
          className="p-1 text-main-muted-foreground hover:text-main-text-button-hover-foreground cursor-pointer"
          aria-label="Close dashboards list"
        >
          <span className="material-icons !text-xl">close</span>
        </button>
      </div>

      <div className="overflow-y-auto overscroll-contain" style={{ maxHeight: 400 }}>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="loading-spinner w-6 h-6 rounded-full border-2 border-gray-200 border-t-2 border-t-blue-500" />
          </div>
        ) : !dashboards || dashboards.length === 0 ? (
          <div className="px-4 py-8 text-sm text-main-muted-foreground text-center">
            No dashboards found.
          </div>
        ) : (
          <div className="py-2">
            {dashboards
              .slice()
              .sort((a, b) => {
                const aTime = a.last_viewed_dt ? new Date(a.last_viewed_dt).getTime() : 0;
                const bTime = b.last_viewed_dt ? new Date(b.last_viewed_dt).getTime() : 0;
                return bTime - aTime;
              })
              .map(dashboard => {
                const title = dashboard.title || "Untitled Dashboard";
                const activityLabel = dashboard.last_viewed_dt
                  ? `Viewed ${moment(dashboard.last_viewed_dt).fromNow()}`
                  : dashboard.created_dt
                  ? `Created ${moment(dashboard.created_dt).fromNow()}`
                  : "";
                const creatorLabel = dashboard.is_owner
                  ? "Created by you"
                  : `Created by ${dashboard.owner_full_name || dashboard.owner_email}`;

                const handleDashboardClick = () => {
                  const url = new URL(
                    `/dashboards/${dashboard.web_dashboard_id}`,
                    window.location.origin
                  );
                  window.open(url.toString(), "_blank");
                };

                return (
                  <button
                    key={dashboard.web_dashboard_id}
                    type="button"
                    onClick={handleDashboardClick}
                    className="w-full px-4 py-3 flex items-center gap-3 hover:bg-main-text-button-hover-background text-left cursor-pointer"
                  >
                    <DashboardRowThumbnail screenshotUrl={dashboard.screenshot_url} title={title} />
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="text-sm font-semibold text-main-foreground truncate">
                        {title}
                      </span>
                      <span className="text-xs text-main-muted-foreground truncate">
                        {creatorLabel}
                      </span>
                      {activityLabel && (
                        <span className="text-xs text-main-muted-foreground truncate">
                          {activityLabel}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
          </div>
        )}
      </div>

      <div className="border-t border-main-border px-4 py-3 sticky bottom-0 bg-main-background">
        <a
          href="/dashboard/web-dashboards"
          target="_blank"
          rel="noopener noreferrer"
          className="h-9 px-5 text-sm font-semibold text-primary-button-foreground bg-primary-button-background rounded-lg hover:bg-primary-button-hover-background disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer transition-colors shadow-sm flex justify-center items-center gap-2"
        >
          All Dashboards
          <NewWindow fill="currentColor" />
        </a>
      </div>
    </MobileBottomSheet>
  );
};
